import { CUSTOM_EVENT_PROPERTIES } from "@modernatx/tracking/lib/get-custom-event-properties";
import { isEqual } from "lodash";
import React from "react";

type CustomEventProperty = (typeof CUSTOM_EVENT_PROPERTIES)[number];

type AppStateProperty = Record<string, string | number | boolean | null>;

type AppState = {
  [K in CustomEventProperty]: K extends "app-state"
    ? AppStateProperty | null
    : string | number | boolean | null;
};

interface TrackAppStateContextType {
  updateAppState: (stateUpdate: Partial<AppState>) => void;
}

const TrackAppStateContext = React.createContext<TrackAppStateContextType>({
  updateAppState: () => {}
});

const getAppStateValue = (value: AppStateProperty): string | null => {
  const result = Object.entries(value)
    .filter(([, val]) => val !== null && val !== undefined)
    .map(([key, val]) => `${key}=${val}`)
    .join(",");

  return result || null;
};

export const TrackAppStateProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [appState, setAppState] = React.useState<Partial<AppState>>({});

  const updateAppState = React.useCallback((stateUpdate: Partial<AppState>) => {
    setAppState((prevState) => {
      const mergedAppState = {
        ...(prevState["app-state"] ?? {}),
        ...(stateUpdate["app-state"] ?? {})
      };

      return {
        ...prevState,
        ...stateUpdate,
        "app-state": mergedAppState
      };
    });
  }, []);

  const dataAttributes = React.useMemo(
    () =>
      Object.entries(appState).reduce(
        (acc: Record<string, string>, [key, value]) => {
          if (value === null || value === undefined) {
            return acc;
          }

          const property = `data-track-${key}`;
          const attrValue =
            key === "app-state" ? getAppStateValue(value as AppStateProperty) : String(value);

          if (attrValue) {
            acc[property] = attrValue;
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    [appState]
  );

  return (
    <TrackAppStateContext.Provider value={{ updateAppState }}>
      <div id="track-app-state" style={{ display: "none" }} {...dataAttributes} />
      {children}
    </TrackAppStateContext.Provider>
  );
};

// This function is used to memoize the props map to avoid unnecessary re-renders.
// It is necessary because the prop is used in a useEffect hook, and if the prop changes,
// the effect will run again.
//
// We handle memoization here to avoid requiring it in the component that uses useTrackAppState.
const useMemoizedPropsMap = (propsMap: Partial<AppState>) => {
  const propsMapRef = React.useRef<Partial<AppState>>(propsMap);

  const hasNewProps = () => !isEqual(propsMapRef.current, propsMap);
  if (hasNewProps()) {
    propsMapRef.current = propsMap;
  }

  return propsMapRef.current;
};

export const useTrackAppState = (propsMap: Partial<AppState>, removeOnDismount: boolean = true) => {
  const context = React.useContext(TrackAppStateContext);
  const memoizedPropsMap = useMemoizedPropsMap(propsMap);

  if (!context) {
    throw new Error("useTrackAppState must be used within a TrackAppStateProvider");
  }

  const { updateAppState } = context;

  React.useEffect(() => {
    updateAppState(memoizedPropsMap);

    return () => {
      if (removeOnDismount) {
        updateAppState(
          Object.keys(memoizedPropsMap).reduce((acc, key) => {
            acc[key as keyof AppState] = null;
            return acc;
          }, {} as Partial<AppState>)
        );
      }
    };
  }, [updateAppState, memoizedPropsMap, removeOnDismount]);
};
